import { MutableRefObject, useCallback } from 'react';

import { Box } from '@mui/material';
import { GridColDef, GridRowId, GridRowSelectionModel, GridRowsProp } from '@mui/x-data-grid-pro';

import { PanelGrid } from 'components/grid';
import { useQueryUsuarios } from 'pages/cadastro/permissoes/hooks';
import { UsuariosProps } from 'pages/cadastro/permissoes/types';

type UsuariosModalGrid = {
    query: string;
    selectionModel: GridRowSelectionModel;
    setSelectionModel: (model: GridRowSelectionModel) => void;
    usuariosCacheRef: MutableRefObject<Map<number, UsuariosProps>>;
};

/**
 * Retorna um array de objetos com os dados dos usuários.
 */
const getRows = (data: UsuariosProps[] = []): GridRowsProp =>
    data.map((item) => ({
        id: item.idUsuario,
        dsUsuario: item.dsUsuario,
    }));

/**
 * Colunas da tabela de usuários.
 */
const columns: GridColDef[] = [
    { field: 'id', headerName: 'Código', width: 80 },
    { field: 'dsUsuario', headerName: 'Usuário', flex: 1, minWidth: 100 },
];

export function PermissoesUsuariosModalGrid({ query, selectionModel, setSelectionModel, usuariosCacheRef }: UsuariosModalGrid) {
    const { isFetching, data, refetch } = useQueryUsuarios({ query });

    /**
     * Ao clicar numa linha ou cabeçalho, adiciona ou remove os usuários selecionados.
     * Converte seleções do tipo exclude (Select All) para include e
     * alimenta o cache com os dados da página atual.
     */
    const handleRowSelection = useCallback(
        (newSelectionModel: GridRowSelectionModel) => {
            // Atualiza o cache com os dados da página atual
            const pageData = data?.data ?? [];

            pageData.forEach((usuario) => {
                if (newSelectionModel.ids.has(usuario.idUsuario)) {
                    usuariosCacheRef.current.set(usuario.idUsuario, usuario);
                } else {
                    usuariosCacheRef.current.delete(usuario.idUsuario);
                }
            });

            setSelectionModel(newSelectionModel);
        },
        [data?.data, setSelectionModel, usuariosCacheRef],
    );

    return (
        <Box sx={{ height: 500 }}>
            <PanelGrid
                checkboxSelection
                keepNonExistentRowsSelected
                rows={getRows(data?.data)}
                rowsCount={data?.total ?? 0}
                reload={refetch}
                columns={columns}
                autoHeight={false}
                loading={isFetching}
                selectionModel={selectionModel}
                onClickRow={handleRowSelection}
            />
        </Box>
    );
}
